import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import type { PlanungAssignment, PlanungFeinplanungRequestSummary } from "@/lib/montageplanung";
import { AssignmentDetailsContent } from "./AssignmentDetailsContent";
import { FeinplanungRequestApprovalCard } from "./FeinplanungRequestApprovalCard";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

export interface AssignmentViewDialogProps {
  open: boolean;
  onClose: () => void;
  assignment: PlanungAssignment | null;
  rowLabel?: string | null;
  canEdit?: boolean;
  onEdit?: () => void;
  onHistory?: () => void;
  canEditFeinplanung?: boolean;
  onFeinplanung?: () => void;
  canRequestFeinplanung?: boolean;
  feinplanungRequestPending?: boolean;
  onRequestFeinplanung?: () => void;
  requestingFeinplanung?: boolean;
  openFeinplanungRequest?: PlanungFeinplanungRequestSummary | null;
  onApproveFeinplanungRequest?: () => void;
  onRejectFeinplanungRequest?: () => void;
  reviewingFeinplanungRequest?: boolean;
  approvingFeinplanungRequest?: boolean;
  rejectingFeinplanungRequest?: boolean;
}

export function AssignmentViewDialog({
  open,
  onClose,
  assignment,
  rowLabel,
  canEdit,
  onEdit,
  onHistory,
  canEditFeinplanung,
  onFeinplanung,
  canRequestFeinplanung,
  feinplanungRequestPending,
  onRequestFeinplanung,
  requestingFeinplanung,
  openFeinplanungRequest,
  onApproveFeinplanungRequest,
  onRejectFeinplanungRequest,
  reviewingFeinplanungRequest,
  approvingFeinplanungRequest,
  rejectingFeinplanungRequest,
}: AssignmentViewDialogProps) {
  if (!assignment) return null;

  const showFeinplanungRequestCard =
    (canRequestFeinplanung && onRequestFeinplanung) || feinplanungRequestPending;
  const showApprovalCard =
    openFeinplanungRequest &&
    onApproveFeinplanungRequest &&
    onRejectFeinplanungRequest;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      showCloseButton={false}
      panelClassName="overflow-hidden rounded-ios-lg border border-separator shadow-ios-md ring-1 ring-black/5"
      bodyClassName="px-4 py-4 sm:px-5"
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          {canEdit && onEdit ? (
            <Button type="button" variant="secondary" onClick={onEdit} className="mr-auto">
              Bearbeiten
            </Button>
          ) : null}
          {onHistory ? (
            <Button
              type="button"
              variant="secondary"
              onClick={onHistory}
              className={canEdit && onEdit ? undefined : "mr-auto"}
            >
              Änderungsverlauf
            </Button>
          ) : null}
          {canEditFeinplanung && onFeinplanung ? (
            <Button
              type="button"
              variant="primary"
              onClick={onFeinplanung}
              className={canEdit && onEdit ? undefined : "mr-auto"}
            >
              Feinplanung bearbeiten
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={onClose} className="w-full sm:w-auto">
            Schließen
          </Button>
        </div>
      }
    >
      <AssignmentDetailsContent assignment={assignment} rowLabel={rowLabel} />
      {showApprovalCard ? (
        <div className="mt-4">
          <FeinplanungRequestApprovalCard
            request={openFeinplanungRequest}
            onApprove={onApproveFeinplanungRequest}
            onReject={onRejectFeinplanungRequest}
            busy={reviewingFeinplanungRequest}
            approving={approvingFeinplanungRequest}
            rejecting={rejectingFeinplanungRequest}
          />
        </div>
      ) : null}
      {showFeinplanungRequestCard ? (
        <div className="mt-4 rounded-ios-md border border-separator bg-secondary/40 px-3 py-3">
          {canRequestFeinplanung && onRequestFeinplanung ? (
            <>
              <p className="mb-3 text-sm text-label-secondary">
                Für diesen Einsatz können Sie beim Büro eine Feinplanung beantragen.
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={onRequestFeinplanung}
                loading={requestingFeinplanung}
                className="w-full sm:w-auto"
              >
                Feinplanung beantragen
              </Button>
            </>
          ) : feinplanungRequestPending ? (
            <p className="text-sm font-medium text-status-orange">
              Feinplanung beantragt – wartet auf Freigabe durch das Büro.
            </p>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}
